import { useState, useEffect, useRef } from 'react';
import { Search, Package, FileText, X, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { mockAssets, mockTransactions } from '@/lib/mock-data';
import { cn } from '@/lib/utils';

interface SearchResult {
  id: string;
  type: 'asset' | 'transaction';
  title: string;
  subtitle: string;
  metadata?: string;
  badge?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
  imageUrl?: string;
  route: string;
}

export function GlobalSearch(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent searches', e);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save recent searches to localStorage
  const saveRecentSearch = (result: SearchResult) => {
    const updated = [
      result,
      ...recentSearches.filter((r) => r.id !== result.id),
    ].slice(0, 5); // Keep only last 5
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  // Perform search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    const query = searchQuery.toLowerCase();
    const searchResults: SearchResult[] = [];

    // Search assets
    const assetResults = mockAssets
      .filter(
        (asset) =>
          asset.name?.toLowerCase().includes(query) ||
          asset.assetId?.toLowerCase().includes(query) ||
          asset.serialNumber?.toLowerCase().includes(query) ||
          asset.category?.toLowerCase().includes(query) ||
          asset.location?.toLowerCase().includes(query)
      )
      .slice(0, 5)
      .map((asset) => ({
        id: `asset-${asset.id}`,
        type: 'asset' as const,
        title: asset.name || 'Unnamed Asset',
        subtitle: `${asset.assetId || 'N/A'} • ${asset.category || 'Uncategorized'}`,
        metadata: asset.location || '',
        badge: asset.status ? asset.status.charAt(0).toUpperCase() + asset.status.slice(1) : 'Unknown',
        badgeVariant: (
          asset.status === 'active' ? 'default' :
          asset.status === 'maintenance' ? 'secondary' :
          asset.status === 'disposed' ? 'destructive' : 'outline'
        ) as 'default' | 'secondary' | 'destructive' | 'outline',
        imageUrl: asset.imageUrl,
        route: `/assets?id=${asset.id}`,
      }));

    // Search transactions
    const transactionResults = mockTransactions
      .filter(
        (txn) =>
          txn.assetId?.toLowerCase().includes(query) ||
          txn.assetName?.toLowerCase().includes(query) ||
          txn.type?.toLowerCase().includes(query) ||
          txn.batchId?.toLowerCase().includes(query)
      )
      .slice(0, 5)
      .map((txn) => ({
        id: `transaction-${txn.id}`,
        type: 'transaction' as const,
        title: txn.assetName || 'Unnamed Transaction',
        subtitle: `${txn.assetId || 'N/A'} • ${txn.type || 'Unknown'}`,
        metadata: `${txn.currency || ''} ${txn.amount?.toLocaleString() || '0'} • ${new Date(txn.transactionDate).toLocaleDateString('en-GB')}`,
        badge: txn.status ? txn.status.charAt(0).toUpperCase() + txn.status.slice(1) : 'Unknown',
        badgeVariant: (
          txn.status === 'posted' ? 'default' :
          txn.status === 'approved' ? 'secondary' :
          txn.status === 'draft' ? 'outline' : 'outline'
        ) as 'default' | 'secondary' | 'destructive' | 'outline',
        route: `/enquiry?id=${txn.id}`,
      }));

    searchResults.push(...assetResults, ...transactionResults);
    setResults(searchResults);
    setSelectedIndex(0);
  }, [searchQuery]);

  // Handle keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const displayResults = searchQuery ? results : recentSearches;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % displayResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + displayResults.length) % displayResults.length);
    } else if (e.key === 'Enter' && displayResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(displayResults[selectedIndex]);
    }
  };

  const handleSelect = (result: SearchResult) => {
    saveRecentSearch(result);
    setOpen(false);
    setSearchQuery('');
    navigate(result.route);
  };

  const assetResults = results.filter((r) => r.type === 'asset');
  const transactionResults = results.filter((r) => r.type === 'transaction');

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search assets, transactions..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="h-9 w-full pl-9 pr-20 text-sm"
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => {
                setSearchQuery('');
                inputRef.current?.focus();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:inline-flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </div>

      {/* Dropdown results */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[500px] overflow-y-auto rounded-md border bg-popover shadow-lg">
          {/* Recent searches */}
          {!searchQuery && recentSearches.length > 0 && (
            <div className="p-2">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-xs font-medium text-muted-foreground">Recent Searches</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 text-xs hover:bg-accent"
                  onClick={clearRecentSearches}
                >
                  Clear
                </Button>
              </div>
              <div className="space-y-1">
                {recentSearches.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent",
                      selectedIndex === index && "bg-accent"
                    )}
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border bg-muted">
                      {result.type === 'asset' ? (
                        result.imageUrl ? (
                          <img
                            src={result.imageUrl}
                            alt={result.title}
                            className="h-full w-full rounded-md object-cover"
                          />
                        ) : (
                          <Package className="h-5 w-5 text-muted-foreground" />
                        )
                      ) : (
                        <FileText className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-sm">{result.title}</span>
                        {result.badge && (
                          <Badge variant={result.badgeVariant} className="text-xs flex-shrink-0">
                            {result.badge}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{result.subtitle}</span>
                        {result.metadata && (
                          <>
                            <span className="flex-shrink-0">•</span>
                            <span className="truncate">{result.metadata}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Clock className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search results */}
          {searchQuery && (
            <>
              {results.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No results found for "{searchQuery}"
                </div>
              ) : (
                <div className="p-2">
                  {/* Asset results */}
                  {assetResults.length > 0 && (
                    <div className="mb-2">
                      <div className="px-2 py-1.5">
                        <span className="text-xs font-medium text-muted-foreground">Assets</span>
                      </div>
                      <div className="space-y-1">
                        {assetResults.map((result, index) => (
                          <button
                            key={result.id}
                            onClick={() => handleSelect(result)}
                            className={cn(
                              "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent",
                              selectedIndex === index && "bg-accent"
                            )}
                          >
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border bg-muted">
                              {result.imageUrl ? (
                                <img
                                  src={result.imageUrl}
                                  alt={result.title}
                                  className="h-full w-full rounded-md object-cover"
                                />
                              ) : (
                                <Package className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                              <div className="flex items-center gap-2">
                                <span className="truncate font-medium text-sm">{result.title}</span>
                                {result.badge && (
                                  <Badge variant={result.badgeVariant} className="text-xs flex-shrink-0">
                                    {result.badge}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="truncate">{result.subtitle}</span>
                                {result.metadata && (
                                  <>
                                    <span className="flex-shrink-0">•</span>
                                    <span className="truncate">{result.metadata}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Transaction results */}
                  {transactionResults.length > 0 && (
                    <div>
                      <div className="px-2 py-1.5">
                        <span className="text-xs font-medium text-muted-foreground">Transactions</span>
                      </div>
                      <div className="space-y-1">
                        {transactionResults.map((result, index) => {
                          const actualIndex = assetResults.length + index;
                          return (
                            <button
                              key={result.id}
                              onClick={() => handleSelect(result)}
                              className={cn(
                                "flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent",
                                selectedIndex === actualIndex && "bg-accent"
                              )}
                            >
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border bg-muted">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                                <div className="flex items-center gap-2">
                                  <span className="truncate font-medium text-sm">{result.title}</span>
                                  {result.badge && (
                                    <Badge variant={result.badgeVariant} className="text-xs flex-shrink-0">
                                      {result.badge}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span className="truncate">{result.subtitle}</span>
                                  {result.metadata && (
                                    <>
                                      <span className="flex-shrink-0">•</span>
                                      <span className="truncate">{result.metadata}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Empty state */}
          {!searchQuery && recentSearches.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Start typing to search assets and transactions...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
